import { ReportMetadata, ServiceItem, UserProfile } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { getServiceLog, mirrorEntriesToJournal } from './serviceJournal';
import { FlatMonthBookingDraft, planFlatMonthBooking } from './flatRateBooking';

/**
 * Schreibpfad der MONATSBUCHUNG aus dem Rechnungsweg (Anlagedialog) — die
 * einzige Stelle ausserhalb des Projekts, die sie persistiert.
 *
 * Die REGELN liegen daneben in `flatRateBooking.ts` und sind ohne Supabase
 * pruefbar; hier steht nur, was ohne Datenbank nicht zu haben ist. Dasselbe
 * Muster wie flatRateSwitchService (und serviceWaiverService davor).
 *
 * Frisch geladen wird IMMER, und der Schluessel-Riegel laeuft ERNEUT gegen
 * den frischen Stand (planFlatMonthBooking prueft selbst): der Dialog rechnet
 * auf einem Abzug vom letzten Refresh, und zwischen Anzeigen und Klick kann
 * derselbe Monat aus einer zweiten Ansicht gebucht worden sein — genau die
 * Doppelbuchung, die der FLAT-Schluessel verhindern soll.
 *
 * Das Projekt in der App-Ansicht bucht NICHT hierueber, sondern ueber seinen
 * vorhandenen onUpdate-Pfad (useReportActions.handleBookFlatMonth): dort haelt
 * App.handleUpdateReport den Sync-Anker nach, und ein zweiter Direkt-Save im
 * offenen Projekt wuerde genau den Konflikt bauen, den der Anker verhindert.
 */

const actorNameOf = (user: UserProfile | null | undefined): string =>
    user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Buchhaltung' : 'Buchhaltung';

export interface FlatMonthBookCommand {
    projectId: string;
    draft: FlatMonthBookingDraft;
    user: UserProfile | null;
}

export interface FlatMonthBookOutcome {
    ok: boolean;
    /** Fuer die Oberflaeche: was schiefging bzw. was gebucht wurde. */
    message: string;
}

/** Einen offenen Pauschalmonat buchen (Protokolleintrag 'FLAT-YYYY-MM'). */
export const bookFlatMonthOnCloud = async (
    command: FlatMonthBookCommand,
): Promise<FlatMonthBookOutcome> => {
    let fullProject: any;
    try {
        fullProject = await loadProjectById(command.projectId);
    } catch (e: any) {
        return { ok: false, message: `Projekt konnte nicht geladen werden: ${e?.message || e}` };
    }
    if (!fullProject) return { ok: false, message: 'Projekt nicht gefunden.' };

    const meta = fullProject.data?.metadata as ReportMetadata | undefined;
    const globalData = meta?.globalData;
    if (!meta || !globalData) {
        return { ok: false, message: 'Das Projekt hat keine lesbaren Stammdaten.' };
    }
    const services: ServiceItem[] = globalData.services || [];
    const serviceLog = getServiceLog({ metadata: meta });

    const now = new Date().toISOString();
    const result = planFlatMonthBooking(
        {
            services,
            serviceLog,
            startDate: globalData.startDate,
            endDate: globalData.endDate,
            today: now.slice(0, 10),
        },
        command.draft,
        {
            entryId: crypto.randomUUID(),
            now,
            actorName: actorNameOf(command.user),
            actorId: command.user?.id,
        },
    );
    // Laut und ohne Schreibvorgang scheitern (dieselbe Begruendung wie bei
    // Verzicht und Umstellung): wer glaubt, gebucht zu haben, fordert den
    // Monat auf keiner Rechnung mehr an.
    if (!result.ok || !result.plan) {
        return { ok: false, message: result.error || 'Die Monatsbuchung konnte nicht geplant werden.' };
    }
    const plan = result.plan;

    try {
        await saveProjectToCloud(
            {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...globalData,
                        services: plan.services,
                        serviceLog: [plan.logEntry, ...serviceLog],
                    },
                },
            },
            fullProject.id,
        );
    } catch (e: any) {
        return { ok: false, message: `Speichern fehlgeschlagen: ${e?.message || e}` };
    }

    // Zweitschrift ins Journal — erst nach dem BESTAETIGTEN Speichern und
    // ausserhalb des try darueber: das Anhaengen darf den Ausgang dieses
    // Vorgangs nicht mehr aendern.
    await mirrorEntriesToJournal({
        projectId: fullProject.id,
        entries: [plan.logEntry],
        previousLog: serviceLog,
        source: 'flat_rate',
        linkedProjectConfig: globalData.linkedProjectConfig ?? null,
    });

    return {
        ok: true,
        message: `${plan.label} gebucht (Menge ${plan.logEntry.quantity.toLocaleString('de-DE')}).`,
    };
};
